import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Clock, Navigation, Phone, Star, Waves } from "lucide-react";
import business from "../data/business.json";
import { PhotoSlot } from "./PhotoSlot";
import { getOpenStatus, todayHoursLabel } from "../lib/hours";

const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
    const status = getOpenStatus();
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
    const bgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.12]);
    const bgProps = reduce ? undefined : { y: bgY, scale: bgScale };

    const rise = (delay) => ({
        initial: reduce ? false : { opacity: 0, y: 26 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.7, ease: EASE, delay },
    });

    return (
        <section
            id="top"
            ref={ref}
            className="relative min-h-[92vh] flex items-end overflow-hidden bg-superficie"
            data-testid="hero"
        >
            <motion.div className="absolute inset-0" style={bgProps} aria-hidden="true">
                <PhotoSlot slotId="hero" icon={Waves} fill eager caption={false} className="[&_svg]:opacity-40" />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-carbone via-carbone/55 to-carbone/25" aria-hidden="true" />
            <div className="absolute inset-0 bg-gradient-to-r from-carbone/70 via-transparent to-transparent" aria-hidden="true" />
            <div className="container-pub relative z-10 w-full pb-14 pt-44">
                <motion.p className="eyebrow" {...rise(0.05)}>
                    {business.address.street} · {business.address.city} ({business.address.province})
                </motion.p>
                <h1 className="font-heading font-semibold uppercase tracking-wide text-4xl sm:text-5xl lg:text-6xl mt-2 max-w-3xl">
                    {["River Pub", "Quarrata"].map((line, i) => (
                        <span key={line} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
                            <motion.span
                                className="block will-change-transform"
                                initial={reduce ? false : { y: "112%" }}
                                animate={{ y: "0%" }}
                                transition={{ duration: 0.95, ease: EASE, delay: 0.15 + i * 0.14 }}
                            >
                                {line}
                            </motion.span>
                        </span>
                    ))}
                </h1>
                <motion.p className="mt-3 text-lg sm:text-xl text-avorio/90 max-w-xl" {...rise(0.5)}>
                    Pub con giardino, birre, cocktail e musica dal vivo
                </motion.p>
                <motion.div className="mt-4" {...rise(0.6)}>
                    <span className="chip" data-testid="hero-rating-chip">
                        <Star className="w-4 h-4 text-ambra" fill="currentColor" strokeWidth={0} />
                        4,4 · 493 recensioni su Google
                    </span>
                </motion.div>
                <motion.div className="mt-7 flex flex-wrap gap-3" {...rise(0.7)}>
                    <a className="btn btn-amber" href={business.phone.href} data-testid="hero-call-btn">
                        <Phone className="w-[18px] h-[18px]" /> Chiama per prenotare
                    </a>
                    <a
                        className="btn btn-outline"
                        href={business.links.mapsDirections}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="hero-directions-btn"
                    >
                        <Navigation className="w-[18px] h-[18px]" /> Come arrivare
                    </a>
                </motion.div>
                <motion.p
                    className="mt-5 flex items-center gap-2 text-sm text-avorio/75 tabular"
                    {...rise(0.8)}
                    data-testid="hero-today-line"
                >
                    <Clock className="w-4 h-4 text-ambra shrink-0" />
                    <span>
                        {todayHoursLabel()} · {status.label}
                    </span>
                </motion.p>
            </div>
        </section>
    );
};
